import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import React, { useEffect, useRef } from 'react';

import { type TextElement, isPlaceholderText, useTextEditorStore } from './useTextEditorStore';

interface NativeTextareaProps {
  element: TextElement;
  onClose: () => void;
}

export function NativeTextarea({ element, onClose }: NativeTextareaProps) {
  const updateElement = useTextEditorStore((state) => state.updateElement);
  const deleteElement = useTextEditorStore((state) => state.deleteElement);
  const setActiveEditor = useTextEditorStore((state) => state.setActiveEditor);

  const startsAsPlaceholder = useRef(isPlaceholderText(element.text));

  const editor = useEditor({
    // StarterKit already includes Underline; do not import the standalone
    // extension or TipTap warns about duplicate extension names.
    extensions: [StarterKit.configure({ strike: false }), TextStyle, Color],
    content: element.text,
    autofocus: startsAsPlaceholder.current ? 'all' : 'end',
    editorProps: {
      attributes: {
        class: 'outline-none border-none min-h-full whitespace-pre-wrap break-words m-0 p-0',
      },
    },
    onUpdate: ({ editor }) => {
      // skipHistory: TipTap's own undo stack handles per-character undo while
      // typing. Pushing one store snapshot per keystroke would flood the undo
      // history (100-char headline = 100 snapshots) with no UX value. The
      // editor commits ONE store snapshot on blur via the next updateElement
      // call (without the flag), so element-level operations (delete, move)
      // can still undo cleanly.
      updateElement(element.id, { text: editor.getHTML() }, { skipHistory: true });
    },
    onBlur: ({ editor }) => {
      // Auto-clean empty elements — but NOT if a save is in progress.
      // IMPORTANT: Read isSavingGlobal via getState(), NOT a reactive hook.
      // TipTap's useEditor captures callbacks at creation time — a hook
      // subscription would always see the initial value (false).
      const plain = editor.getText().trim();
      const saving = useTextEditorStore.getState().isSavingGlobal;
      if (plain.length === 0 && !saving) {
        deleteElement(element.id);
      }
      onClose();
      setActiveEditor(null);
    },
    onSelectionUpdate: ({ editor }) => {
      setActiveEditor(editor);
    },
    onFocus: ({ editor }) => {
      setActiveEditor(editor);
    },
  });

  useEffect(() => {
    return () => {
      setActiveEditor(null);
    };
  }, [setActiveEditor]);

  // TipTap's `autofocus: 'end'` config sometimes loses the race with parent
  // re-renders. Force focus on the next paint so users can start typing
  // immediately after double-click — without it, the cursor doesn't land
  // and keystrokes go to the global window handler.
  useEffect(() => {
    if (!editor) return;
    const id = window.requestAnimationFrame(() => {
      if (startsAsPlaceholder.current) editor.chain().focus().selectAll().run();
      else editor.chain().focus('end').run();
    });
    return () => window.cancelAnimationFrame(id);
  }, [editor]);

  // One-way sync: store → TipTap. When toolbar buttons update the store
  // (fontWeight, isUnderline, isItalic), push those changes into TipTap's
  // internal state so the inline editor reflects them immediately.
  useEffect(() => {
    if (!editor) return;

    // Build a single chain — only call .focus() if editor isn't already focused
    // to avoid destroying the user's text selection when toggling toolbar buttons.
    const chain = editor.isFocused ? editor.chain() : editor.chain().focus();
    let needsRun = false;

    // Bold: TipTap uses semantic <strong>, we map from fontWeight >= 700
    const shouldBeBold = (element.fontWeight ?? 400) >= 700;
    const isTipTapBold = editor.isActive('bold');
    if (shouldBeBold !== isTipTapBold) {
      if (shouldBeBold) chain.setBold();
      else chain.unsetBold();
      needsRun = true;
    }

    // Underline
    const isTipTapUnderline = editor.isActive('underline');
    if (element.isUnderline !== isTipTapUnderline) {
      if (element.isUnderline) chain.setUnderline();
      else chain.unsetUnderline();
      needsRun = true;
    }

    // Italic
    const isTipTapItalic = editor.isActive('italic');
    if (element.isItalic !== isTipTapItalic) {
      if (element.isItalic) chain.setItalic();
      else chain.unsetItalic();
      needsRun = true;
    }

    if (needsRun) chain.run();
  }, [editor, element.fontWeight, element.isUnderline, element.isItalic]);

  // Handle Escape key — preventDefault stops the parent dialog from also
  // dismissing, stopPropagation prevents the MediaStudioTextOverlay global
  // keydown handler from seeing the event.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose]);

  // Align the DOM element over the Konva canvas text. The parent
  // stageWrapRef already carries `transform: scale(zoom)`, so all children
  // are visually scaled by the zoom factor. Using raw element coordinates
  // here (no `* stageScale`) avoids double-application of the zoom.
  const style: React.CSSProperties = {
    position: 'absolute',
    top: `${element.y}px`,
    left: `${element.x}px`,
    width: `${element.width}px`,
    minHeight: `${element.fontSize * (element.lineHeight ?? 1.2)}px`,
    fontSize: `${element.fontSize}px`,
    fontFamily: element.fontFamily,
    fontWeight: element.fontWeight ?? 400,
    fontStyle: element.isItalic ? 'italic' : 'normal',
    textDecoration: element.isUnderline ? 'underline' : 'none',
    textAlign: element.textAlign ?? 'left',
    textTransform: element.textTransform ?? 'none',
    lineHeight: `${element.lineHeight ?? 1.2}`,
    letterSpacing: `${element.letterSpacing ?? 0}em`,
    color: typeof element.fill === 'string' ? element.fill : (element.fill.colors[0] ?? '#ffffff'),
    transform: `rotate(${element.rotation}deg)`,
    transformOrigin: 'top left',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    overflow: 'visible',
    padding: 0,
    margin: 0,
    zIndex: 50,
  };

  return (
    <div style={style} className="outline-none border-none">
      <EditorContent
        editor={editor}
        className="outline-none border-none focus:outline-none min-h-full"
      />
    </div>
  );
}
